import { useRef, useState, type ChangeEvent } from 'react'

import { uploadPeopleImage } from '../../api/uploads'

type Props = {
  value: string
  onChange: (url: string) => void
  label?: string
  onUploaded?: () => void
  onError?: (message: string) => void
}

export default function OfferImageUploader({
  value,
  onChange,
  label = 'Offer image',
  onUploaded,
  onError,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const uploaded = await uploadPeopleImage(file)
      onChange(uploaded.url)
      onUploaded?.()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed'
      setError(String(msg))
      onError?.(String(msg))
    } finally {
      setUploading(false)
      if (e.target) e.target.value = ''
    }
  }

  return (
    <div>
      <label className="text-xs text-white/60">{label}</label>
      <div className="mt-1 rounded-lg border border-white/10 bg-black/20 p-2">
        {value ? (
          <div className="overflow-hidden rounded-lg border border-white/10">
            <img src={value} alt="Offer" className="h-32 w-full object-cover" />
          </div>
        ) : (
          <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-white/15 bg-white/5 text-xs text-white/50">
            No image selected
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {uploading ? 'Uploading...' : 'Upload image'}
          </button>
          {value ? (
            <button
              type="button"
              onClick={() => onChange('')}
              disabled={uploading}
              className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Remove image
            </button>
          ) : null}
        </div>
        <div className="mt-2">
          <label className="text-[11px] text-white/55">Paste URL fallback</label>
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        {error ? <div className="mt-2 text-xs text-red-300">{error}</div> : null}
      </div>
    </div>
  )
}

